/**
 * Redesign pass B (item 7) — the ONE place the box quantity is set.
 *
 * Lives in the configurator's sticky price bar from step one. The cart
 * drawer and the checkout page mirror the value read-only (checkout links
 * back here via "Edit quantity"), so quantity has a single setting surface
 * while the state itself stays owned by `useConfigurator.cart.qty`.
 *
 * The − / + buttons step by the tier granularity (25 — the spacing of
 * `TIER_BANDS`), the inline input accepts any custom value, and the box's
 * authored floor (`defaults.minQuantity`) is enforced on blur / stepping so
 * mid-typing values are never clobbered.
 *
 * # Chapter 10 (F5 + F4) — the hint line
 *
 * Free typing was always allowed and never signalled: three testers concluded
 * "it looks like you can only choose 25, 50 or 100 — but clients want 87",
 * because the ± 25 buttons dominate the control. The hint under the pill says
 * so in words, reads the typed quantity back, names the band it falls in, and —
 * only when the real resolved prices differ — names the next band and what
 * reaching it saves. The arithmetic and the honesty rules live in
 * `lib/quantityBands.ts`; this component only renders the answer.
 */

import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { buildQuantityBandHint } from '../lib/quantityBands';

interface Props {
  qty: number;
  /** The box's authored minimum quantity (`defaults.minQuantity`); 0/absent → 1. */
  floor?: number;
  /** Step size for the − / + buttons. Defaults to the tier granularity. */
  step?: number;
  onChange: (qty: number) => void;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  /**
   * Price of one box at a hypothetical quantity, for the band hint. Omit it and
   * the hint still names the active band; it just makes no claim about savings
   * (which is also what happens on a flat-priced box).
   */
  unitPriceAt?: (atQty: number) => number;
}

export const QUANTITY_STEP = 25;

export function QuantityStepper({
  qty,
  floor,
  step = QUANTITY_STEP,
  onChange,
  inputRef,
  unitPriceAt,
}: Props) {
  const min = floor && floor > 0 ? floor : 1;
  const hintId = React.useId();

  const decrement = () => onChange(Math.max(min, qty - step));
  // From below the floor (possible mid-typing), + snaps up to the floor first.
  const increment = () => onChange(qty < min ? min : qty + step);

  const hint = buildQuantityBandHint(qty, unitPriceAt ?? (() => 0));

  return (
    <div className="flex flex-col gap-1.5">
      <div
        data-testid="quantity-stepper"
        className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full p-1"
      >
        <button
          type="button"
          onClick={decrement}
          disabled={qty <= min}
          aria-label={`Decrease quantity by ${step} boxes`}
          className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#356d4e] hover:border-[#57c858] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-200 transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>
        <input
          ref={inputRef}
          type="number"
          value={qty}
          min={min}
          aria-label="Number of boxes"
          aria-describedby={hintId}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // Free typing — the state owner clamps to >= 1; the box floor is
            // only enforced on blur so "1" en route to "100" isn't clobbered.
            onChange(Number.isNaN(v) ? 0 : v);
          }}
          onBlur={() => {
            if (qty < min) onChange(min);
          }}
          className="w-14 bg-transparent text-center font-black text-[#356d4e] text-base outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          onClick={increment}
          aria-label={`Increase quantity by ${step} boxes`}
          className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#356d4e] hover:border-[#57c858] transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <p
        id={hintId}
        data-testid="quantity-band-hint"
        aria-live="polite"
        className="text-[11px] font-bold text-gray-400 leading-tight max-w-[16rem]"
      >
        <span className="text-[#444444]">Type any number</span>
        {` · ${qty} ${qty === 1 ? 'box' : 'boxes'}`}
        {hint.bandLabel !== null && ` · ${hint.bandLabel} tier`}
        {hint.nextSaving !== null && (
          <span className="text-[#356d4e]">
            {` · order ${hint.nextSaving.minQty}+ to save ${hint.nextSaving.percent}%`}
          </span>
        )}
      </p>
    </div>
  );
}
